import { toast } from 'sonner';
import { useEffect, useState } from 'react';
import { Info, RefreshCw } from 'lucide-react';

import { Label } from '@/components/ui/label';
import { hasUsableTranscript } from '../utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useEncounter } from '../api/encounters-api';
import { useGenerateDocument } from '../api/documents-api';
import { usePickerTemplates } from '@/features/templates/api/templates-api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface RegenerateDocumentDialogProps {
  defaultTemplateId: string | null;
  encounterId: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  patientId: string | null;
  /**
   * When true, the dialog opens with the existing template pre-selected and
   * the heading nudges toward "Regenerate". When false, the dialog presents
   * itself as "Generate new document". The underlying mutation is the same.
   */
  variant: 'regenerate' | 'new';
}

export function RegenerateDocumentDialog({
  open,
  variant,
  patientId,
  encounterId,
  onOpenChange,
  defaultTemplateId,
}: RegenerateDocumentDialogProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<string>(defaultTemplateId ?? '');
  const [notes, setNotes] = useState('');

  const { data: templatesData } = usePickerTemplates({ active_only: true });
  const templates = templatesData ?? [];

  const generateMutation = useGenerateDocument();

  const { data: encounter, isLoading: isEncounterLoading } = useEncounter(open ? encounterId : undefined);
  const hasTranscript = hasUsableTranscript(encounter?.transcript);

  // Re-sync default when the dialog re-opens against a different source doc.
  useEffect(() => {
    if (open) {
      setSelectedTemplate(defaultTemplateId ?? '');
      setNotes('');
    }
  }, [open, defaultTemplateId]);

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleGenerate = async () => {
    if (!selectedTemplate) {
      toast.error('Please select a document template');
      return;
    }
    try {
      const trimmedNotes = notes.trim();
      await generateMutation.mutateAsync({
        patient_id: patientId,
        encounter_ids: [encounterId],
        template_id: selectedTemplate,
        additional_context: trimmedNotes ? { notes: trimmedNotes } : null,
      });
      toast.success(
        variant === 'regenerate'
          ? 'Regenerating — the new version will appear shortly.'
          : 'Generating — the new document will appear shortly.',
      );
      handleClose();
    } catch (err: unknown) {
      // Show what the API actually said. The generic string this used to throw
      // away is why a clinician reporting "it failed" could tell us nothing:
      // the real cause was an auth error from a misconfigured LLM client, and
      // it never reached anyone's screen.
      const response = (err as { response?: { data?: { detail?: string }; status?: number } })?.response;
      toast.error(response?.data?.detail ?? 'Could not start generation. Please try again.');
    }
  };

  const heading = variant === 'regenerate' ? 'Regenerate Document' : 'Generate New Document';
  const description =
    variant === 'regenerate'
      ? 'Run the template again over the same transcript. The new version is saved as an additional document — the original is preserved.'
      : 'Pick a different template to produce a daughter document (e.g. patient summary, referral letter) from the same encounter transcript.';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            {heading}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!isEncounterLoading && !hasTranscript && (
            <div className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
              This encounter has no transcript yet. Record or add a transcript before regenerating.
            </div>
          )}
          <div className="space-y-2">
            <Label>Document Template</Label>
            <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
              <SelectTrigger>
                <SelectValue placeholder="Select a template..." />
              </SelectTrigger>
              <SelectContent>
                {templates.map((tmpl) => (
                  <SelectItem key={tmpl.id} value={tmpl.id}>
                    {tmpl.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedTemplate && (
            <div className="space-y-2">
              <Label>Additional Notes (Optional)</Label>
              <Textarea
                rows={3}
                value={notes}
                placeholder="Any additional guidance for this run (e.g. 'keep it brief', 'emphasize referral rationale')..."
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          )}

          <div className="flex items-start gap-2 rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              This runs the full pipeline — cleanup, generation, tagging and the proofreader where the template enables
              it. The document appears in the list once it finishes.
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={generateMutation.isPending} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            disabled={!hasTranscript || !selectedTemplate || generateMutation.isPending}
            onClick={handleGenerate}>
            {generateMutation.isPending ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Generate
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
